import { useMemo } from 'react'

import { Contract, Feature } from 'schema/contract'
import PortalDailyDataAccordion, {
  MetricRowConfig,
  TableRowWithDate,
  createDateList,
  formatPercent,
  useQuantityMap,
} from './portal-daily-data-accordion'

const calcRate = (detail: number, list: number) => {
  if (!list) {
    return null
  }

  return Math.round((detail / list) * 10000) / 100
}

const SuumoDailyDataAccordion = ({
  contract,
}: {
  contract?: Contract | Feature
}) => {
  const dates = useMemo(createDateList, [])

  const listPvPcMap = useQuantityMap(contract?.suumo?.listPvForPC)
  const listPvSpMap = useQuantityMap(contract?.suumo?.listPvForSmartphone)
  const areaListPvMap = useQuantityMap(contract?.suumo?.areaAverageListPv)
  const detailPvPcMap = useQuantityMap(contract?.suumo?.detailPvForPC)
  const detailPvSpMap = useQuantityMap(contract?.suumo?.detailPvForSmartphone)
  const areaDetailPvMap = useQuantityMap(contract?.suumo?.areaAverageDetailPv)
  const linkClickMap = useQuantityMap(contract?.suumo?.linkClickCount)
  const favoriteMap = useQuantityMap(contract?.suumo?.favoriteCount)
  const documentRequestMap = useQuantityMap(contract?.suumo?.documentRequest)
  const visitorReservationMap = useQuantityMap(
    contract?.suumo?.visitorReservation
  )

  const rows = useMemo<TableRowWithDate[]>(() => {
    return dates.map<TableRowWithDate>((date) => {
      const key = date.key
      const listPc = listPvPcMap[key] ?? 0
      const listSp = listPvSpMap[key] ?? 0
      const detailPc = detailPvPcMap[key] ?? 0
      const detailSp = detailPvSpMap[key] ?? 0
      const areaList = areaListPvMap[key] ?? 0
      const areaDetail = areaDetailPvMap[key] ?? 0

      return {
        date: date.label,
        listPc,
        listSp,
        listArea: areaList,
        detailPc,
        detailSp,
        detailArea: areaDetail,
        transitionPc: calcRate(detailPc, listPc),
        transitionSp: calcRate(detailSp, listSp),
        transitionTotal: calcRate(detailPc + detailSp, listPc + listSp),
        transitionArea: calcRate(areaDetail, areaList),
        hpClick: linkClickMap[key] ?? 0,
        favorite: favoriteMap[key] ?? 0,
        documentRequest: documentRequestMap[key] ?? 0,
        visitorReservation: visitorReservationMap[key] ?? 0,
      }
    })
  }, [
    areaDetailPvMap,
    areaListPvMap,
    dates,
    detailPvPcMap,
    detailPvSpMap,
    documentRequestMap,
    favoriteMap,
    linkClickMap,
    listPvPcMap,
    listPvSpMap,
    visitorReservationMap,
  ])

  const metricRows = useMemo<MetricRowConfig[]>(
    () => [
      {
        key: 'listPc',
        label: (
          <>
            <span>一覧PV</span>
            <br />
            (PC)
          </>
        ),
      },
      {
        key: 'listSp',
        label: (
          <>
            <span>一覧PV</span>
            <br />
            (スマホ)
          </>
        ),
      },
      {
        key: 'listArea',
        label: (
          <>
            <span>一覧PV</span>
            <br />
            (エリア平均)
          </>
        ),
      },
      {
        key: 'detailPc',
        label: (
          <>
            <span>詳細PV</span>
            <br />
            (PC)
          </>
        ),
      },
      {
        key: 'detailSp',
        label: (
          <>
            <span>詳細PV</span>
            <br />
            (スマホ)
          </>
        ),
      },
      {
        key: 'detailArea',
        label: (
          <>
            <span>詳細PV</span>
            <br />
            (エリア平均)
          </>
        ),
      },
      {
        key: 'transitionPc',
        label: (
          <>
            <span>遷移率</span>
            <br />
            (PC)
          </>
        ),
        formatter: formatPercent,
      },
      {
        key: 'transitionSp',
        label: (
          <>
            <span>遷移率</span>
            <br />
            (スマホ)
          </>
        ),
        formatter: formatPercent,
      },
      {
        key: 'transitionTotal',
        label: (
          <>
            <span>遷移率</span>
            <br />
            (全体)
          </>
        ),
        formatter: formatPercent,
      },
      {
        key: 'transitionArea',
        label: (
          <>
            <span>遷移率</span>
            <br />
            (エリア平均)
          </>
        ),
        formatter: formatPercent,
      },
      {
        key: 'hpClick',
        label: (
          <>
            <span>HPリンク</span>
            <br />
            クリック数
          </>
        ),
      },
      {
        key: 'favorite',
        label: (
          <>
            <span>お気に入り</span>
            <br />
            登録数
          </>
        ),
      },
      { key: 'documentRequest', label: '資料請求数' },
      { key: 'visitorReservation', label: '来場予約数' },
    ],
    []
  )

  if (!contract?.suumo) {
    return null
  }

  return <PortalDailyDataAccordion rows={rows} metricRows={metricRows} />
}

export default SuumoDailyDataAccordion
